import {Link, useNavigate} from 'react-router';

import {dataTableActionT, dataTableColumnT} from '@/lib/types/dataTable.types';
import {SelectedColumnsT} from '@/lib/types/query.types';
import {statsRowT} from '@/lib/types/stats.types';

import {usdFormatter} from '@/lib/helpers';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useAuth} from '@/providers/components/authProvider';

import {StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

/**
 * Admin Orders page
 * This page displays all of the orders in the system and allows the admin to view, edit, and delete them
 * @return {JSX.Element} - Admin Orders page
 */
function AdminOrders() {
  const navigate = useNavigate();
  const {isAdmin} = useAuth();

  // stats for stats component
  const statsRow: statsRowT = [
    {
      title: 'Total Orders',
      value: 10,
      subtitle: 'This Month',
      icon: 'ri-team-line',
      color: 'primary',
      change: 1.03,
      cardClass: 'Users-main-card primary',
    },
    {
      title: 'Active Orders',
      value: 20,
      subtitle: 'This Month',
      icon: 'ri-user-unfollow-line',
      color: 'secondary',
      change: 0.36,
      cardClass: 'Users-main-card secondary',
    },
    {
      title: 'Inactive Orders',
      value: 50,
      subtitle: 'This Month',
      icon: 'ri-service-line',
      color: 'warning',
      change: -1.28,
      cardClass: 'Users-main-card warning',
    },
  ];

  // Data Table -----------------------------------------------------
  const {title, table, filters, columns, searchColumn} = createDataTableConfig({
    title: 'Orders Summary',
    table: 'Order',
    searchColumn: 'order_id',
    columns: [
      'order_id',
      'order_balance',
      'order_delivery_address',
      'order_delivery_city',
      'order_delivery_state',
      'order_delivery_postal_code',
      'order_status',
      'order_payment_method',
      'order_payment_terms',
      'order_payment_received',
      'order_delivery_address2',
      ['Distributor', ['distributor_name', 'distributor_id']],
    ],
  });

  // Helper types to make the code cleaner and DRY
  type DataTableT = typeof table;
  type DataTableColumnsT = typeof columns;
  type DataTableRowT = SelectedColumnsT<DataTableT, DataTableColumnsT>;

  const columnsDisplay: dataTableColumnT<DataTableT, DataTableRowT>[] = [
    {
      header: 'Order ID',
      field: 'order_id',
      render: row => (
        <Link to={`/admin/orders/${row.order_id}`} className="text-primary hover:text-primary dark:text-primary">
          {row.order_id}
        </Link>
      ),
    },
    {
      header: 'Distributor',
      field: 'Distributor.distributor_name',
      render(row) {
        return (
          <Link
            to={`/distributors/${row.Distributor.distributor_id}`}
            className="text-primary hover:text-primary dark:text-primary"
          >
            {row.Distributor.distributor_name}
          </Link>
        );
      },
    },
    {
      header: 'Order Balance',
      field: 'order_balance',
      render: row => row.order_balance && usdFormatter.format(row.order_balance),
    },
    {header: 'Delivery Address', field: 'order_delivery_address'},
    {header: 'City', field: 'order_delivery_city'},
    {header: 'State', field: 'order_delivery_state'},
    {header: 'Postal Code', field: 'order_delivery_postal_code'},
    {header: 'Order Status', field: 'order_status'},
    {header: 'Payment Method', field: 'order_payment_method'},
    {header: 'Payment Terms', field: 'order_payment_terms'},
    {header: 'Payment Received', field: 'order_payment_received'},
    {header: 'Delivery Address 2', field: 'order_delivery_address2'},
  ];

  const actions: dataTableActionT<DataTableT>[] = [
    {
      icon: 'ri-pencil-line',
      color: 'info',
      handler: () => null,
      permission: () => isAdmin,
    },
    {
      icon: 'ri-delete-bin-line',
      color: 'danger',
      handler: () => null,
      permission: () => isAdmin,
    },
  ];

  return (
    <Page currentPage="Orders">
      <StatsRowComponent stats={statsRow} />
      <DataTable
        title={title}
        table={table}
        columns={columns}
        columnsDisplay={columnsDisplay}
        actionsDisplay={actions}
        searchColumn={searchColumn}
        filters={filters}
        onAdd={() => navigate('/admin/orders/new')}
      />
    </Page>
  );
}

export default AdminOrders;
